// Utility functions for managing local storage when users are not authenticated
import api from './api';
import analytics from './analytics';

const LOCAL_TCG_MATCHES_KEY = 'vslogger_local_tcg_matches';
const LOCAL_VGC_MATCHES_KEY = 'vslogger_local_vgc_matches';
const LOCAL_DECKS_KEY = 'vslogger_local_decks';
const LOCAL_TEAMS_KEY = 'vslogger_local_teams';
const LOCAL_ACTIVE_DECK_KEY = 'vslogger_local_active_deck';
const LOCAL_TCG_DRAFT_KEY = 'vslogger_local_tcg_draft';

// Helper to generate unique IDs for local matches
export const generateLocalId = () => {
  return `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// TCG Match Functions
export const getLocalTCGMatches = () => {
  try {
    const matches = localStorage.getItem(LOCAL_TCG_MATCHES_KEY);
    return matches ? JSON.parse(matches) : [];
  } catch (error) {
    console.error('Error getting local TCG matches:', error);
    return [];
  }
};

export const saveLocalTCGMatch = (match) => {
  try {
    const matches = getLocalTCGMatches();
    const newMatch = {
      ...match,
      _id: generateLocalId(),
      createdAt: new Date().toISOString(),
      isLocal: true
    };
    matches.unshift(newMatch);
    localStorage.setItem(LOCAL_TCG_MATCHES_KEY, JSON.stringify(matches));
    analytics.trackLocalMatch('tcg');
    return newMatch;
  } catch (error) {
    console.error('Error saving local TCG match:', error);
    throw error;
  }
};

export const updateLocalTCGMatch = (matchId, updates) => {
  try {
    const matches = getLocalTCGMatches();
    const index = matches.findIndex(m => m._id === matchId);
    if (index !== -1) {
      matches[index] = { ...matches[index], ...updates };
      localStorage.setItem(LOCAL_TCG_MATCHES_KEY, JSON.stringify(matches));
      return matches[index];
    }
    throw new Error('Match not found');
  } catch (error) {
    console.error('Error updating local TCG match:', error);
    throw error;
  }
};

export const deleteLocalTCGMatch = (matchId) => {
  try {
    const matches = getLocalTCGMatches();
    const filtered = matches.filter(m => m._id !== matchId);
    localStorage.setItem(LOCAL_TCG_MATCHES_KEY, JSON.stringify(filtered));
    return true;
  } catch (error) {
    console.error('Error deleting local TCG match:', error);
    throw error;
  }
};

// VGC Match Functions
export const getLocalVGCMatches = () => {
  try {
    const matches = localStorage.getItem(LOCAL_VGC_MATCHES_KEY);
    return matches ? JSON.parse(matches) : [];
  } catch (error) {
    console.error('Error getting local VGC matches:', error);
    return [];
  }
};

export const saveLocalVGCMatch = (match) => {
  try {
    const matches = getLocalVGCMatches();
    const newMatch = {
      ...match,
      _id: generateLocalId(),
      createdAt: new Date().toISOString(),
      isLocal: true
    };
    matches.unshift(newMatch);
    localStorage.setItem(LOCAL_VGC_MATCHES_KEY, JSON.stringify(matches));
    analytics.trackLocalMatch('vgc');
    return newMatch;
  } catch (error) {
    console.error('Error saving local VGC match:', error);
    throw error;
  }
};

export const updateLocalVGCMatch = (matchId, updates) => {
  try {
    const matches = getLocalVGCMatches();
    const index = matches.findIndex(m => m._id === matchId);
    if (index !== -1) {
      matches[index] = { ...matches[index], ...updates };
      localStorage.setItem(LOCAL_VGC_MATCHES_KEY, JSON.stringify(matches));
      return matches[index];
    }
    throw new Error('Match not found');
  } catch (error) {
    console.error('Error updating local VGC match:', error);
    throw error;
  }
};

export const deleteLocalVGCMatch = (matchId) => {
  try {
    const matches = getLocalVGCMatches();
    const filtered = matches.filter(m => m._id !== matchId);
    localStorage.setItem(LOCAL_VGC_MATCHES_KEY, JSON.stringify(filtered));
    return true;
  } catch (error) {
    console.error('Error deleting local VGC match:', error);
    throw error;
  }
};

// Deck Functions
export const getLocalDecks = () => {
  try {
    const decks = localStorage.getItem(LOCAL_DECKS_KEY);
    return decks ? JSON.parse(decks) : [];
  } catch (error) {
    console.error('Error getting local decks:', error);
    return [];
  }
};

export const saveLocalDeck = (deck) => {
  try {
    const decks = getLocalDecks();
    const newDeck = {
      ...deck,
      _id: generateLocalId(),
      createdAt: new Date().toISOString(),
      isLocal: true
    };
    decks.unshift(newDeck);
    localStorage.setItem(LOCAL_DECKS_KEY, JSON.stringify(decks));
    return newDeck;
  } catch (error) {
    console.error('Error saving local deck:', error);
    throw error;
  }
};

export const updateLocalDeck = (deckId, updates) => {
  try {
    const decks = getLocalDecks();
    const index = decks.findIndex(d => d._id === deckId);
    if (index !== -1) {
      decks[index] = { ...decks[index], ...updates };
      localStorage.setItem(LOCAL_DECKS_KEY, JSON.stringify(decks));
      return decks[index];
    }
    throw new Error('Deck not found');
  } catch (error) {
    console.error('Error updating local deck:', error);
    throw error;
  }
};

export const deleteLocalDeck = (deckId) => {
  try {
    const decks = getLocalDecks();
    const filtered = decks.filter(d => d._id !== deckId);
    localStorage.setItem(LOCAL_DECKS_KEY, JSON.stringify(filtered));
    return true;
  } catch (error) {
    console.error('Error deleting local deck:', error);
    throw error;
  }
};

// Team Functions
export const getLocalTeams = () => {
  try {
    const teams = localStorage.getItem(LOCAL_TEAMS_KEY);
    return teams ? JSON.parse(teams) : [];
  } catch (error) {
    console.error('Error getting local teams:', error);
    return [];
  }
};

export const saveLocalTeam = (team) => {
  try {
    const teams = getLocalTeams();
    const newTeam = {
      ...team,
      _id: generateLocalId(),
      createdAt: new Date().toISOString(),
      isLocal: true
    };
    teams.unshift(newTeam);
    localStorage.setItem(LOCAL_TEAMS_KEY, JSON.stringify(teams));
    return newTeam;
  } catch (error) {
    console.error('Error saving local team:', error);
    throw error;
  }
};

export const updateLocalTeam = (teamId, updates) => {
  try {
    const teams = getLocalTeams();
    const index = teams.findIndex(t => t._id === teamId);
    if (index !== -1) {
      teams[index] = { ...teams[index], ...updates };
      localStorage.setItem(LOCAL_TEAMS_KEY, JSON.stringify(teams));
      return teams[index];
    }
    throw new Error('Team not found');
  } catch (error) {
    console.error('Error updating local team:', error);
    throw error;
  }
};

export const deleteLocalTeam = (teamId) => {
  try {
    const teams = getLocalTeams();
    const filtered = teams.filter(t => t._id !== teamId);
    localStorage.setItem(LOCAL_TEAMS_KEY, JSON.stringify(filtered));
    return true;
  } catch (error) {
    console.error('Error deleting local team:', error);
    throw error;
  }
};

// Check if we have any local data
export const hasLocalData = () => {
  const tcgMatches = getLocalTCGMatches();
  const vgcMatches = getLocalVGCMatches();
  const decks = getLocalDecks();
  const teams = getLocalTeams();
  return tcgMatches.length > 0 || vgcMatches.length > 0 || decks.length > 0 || teams.length > 0;
};

// Clear all local data (useful when user logs in)
export const clearLocalData = () => {
  localStorage.removeItem(LOCAL_TCG_MATCHES_KEY);
  localStorage.removeItem(LOCAL_VGC_MATCHES_KEY);
  localStorage.removeItem(LOCAL_DECKS_KEY);
  localStorage.removeItem(LOCAL_TEAMS_KEY);
  localStorage.removeItem(LOCAL_ACTIVE_DECK_KEY);
  localStorage.removeItem(LOCAL_TCG_DRAFT_KEY);
  console.log('Cleared all local storage data');
};

// Sync functions to upload local matches to the database
export const syncLocalMatchesToDatabase = async (api, addTCGMatch, addVGCMatch) => {
  const tcgMatches = getLocalTCGMatches();
  const vgcMatches = getLocalVGCMatches();
  
  let syncedCount = 0;
  let errors = [];

  console.log(`Starting sync: ${tcgMatches.length} TCG matches, ${vgcMatches.length} VGC matches`);

  // Sync TCG matches
  for (const match of tcgMatches) {
    try {
      // Remove local-specific fields before syncing
      const { _id, isLocal, ...matchData } = match;
      
      // Use the context function to add the match
      const result = await addTCGMatch(matchData);
      
      if (result && result.success) {
        syncedCount++;
        console.log(`Successfully synced TCG match: ${match._id}`);
      } else {
        throw new Error(result?.error || 'Failed to sync match');
      }
    } catch (error) {
      console.error(`Failed to sync TCG match ${match._id}:`, error);
      errors.push(`TCG match from ${match.createdAt}: ${error.message}`);
    }
  }

  // Sync VGC matches
  for (const match of vgcMatches) {
    try {
      // Remove local-specific fields before syncing
      const { _id, isLocal, ...matchData } = match;
      
      // Use the context function to add the match
      const result = await addVGCMatch(matchData);
      
      if (result && result.success) {
        syncedCount++;
        console.log(`Successfully synced VGC match: ${match._id}`);
      } else {
        throw new Error(result?.error || 'Failed to sync match');
      }
    } catch (error) {
      console.error(`Failed to sync VGC match ${match._id}:`, error);
      errors.push(`VGC match from ${match.createdAt}: ${error.message}`);
    }
  }

  // Clear local storage only if all matches were synced successfully
  if (errors.length === 0) {
    clearLocalData();
    console.log(`Successfully synced ${syncedCount} matches and cleared local storage`);
  } else {
    console.warn(`Synced ${syncedCount} matches, but ${errors.length} failed. Keeping failed matches in local storage.`);
    
    // Remove only the successfully synced matches from local storage
    // This is more complex, so for now we'll keep all matches if any failed
    // In production, you might want to track which ones succeeded and only remove those
  }

  return {
    syncedCount,
    totalMatches: tcgMatches.length + vgcMatches.length,
    errors
  };
};

// Enhanced sync function to upload all local data (matches, decks, teams) to the database
export const syncLocalDataToDatabase = async (api, addTCGMatch, addVGCMatch, addDeck, addTeam, userId = null) => {
  console.log('Starting local data sync to database...');
  
  const tcgMatches = getLocalTCGMatches();
  const vgcMatches = getLocalVGCMatches();
  const decks = getLocalDecks();
  const teams = getLocalTeams();
  
  let syncedCount = 0;
  let errors = [];
  const successfulSyncs = {
    tcgMatches: [],
    vgcMatches: [],
    decks: [],
    teams: []
  };
  const failedSyncs = {
    tcgMatches: [],
    vgcMatches: [],
    decks: [],
    teams: []
  };

  console.log(`Starting sync: ${tcgMatches.length} TCG matches, ${vgcMatches.length} VGC matches, ${decks.length} decks, ${teams.length} teams`);

  // Sync TCG matches
  for (const match of tcgMatches) {
    try {
      // Remove local-specific fields before syncing
      const { _id, isLocal, ...matchData } = match;
      
      // Use the context function to add the match
      const result = await addTCGMatch(matchData);
      
      if (result && result.success) {
        syncedCount++;
        successfulSyncs.tcgMatches.push(match._id);
        console.log(`Successfully synced TCG match: ${match._id}`);
      } else {
        throw new Error(result?.error || 'Failed to sync match');
      }
    } catch (error) {
      console.error(`Failed to sync TCG match ${match._id}:`, error);
      errors.push(`TCG match from ${match.createdAt}: ${error.message}`);
    }
  }

  // Sync VGC matches
  for (const match of vgcMatches) {
    try {
      // Remove local-specific fields before syncing
      const { _id, isLocal, ...matchData } = match;
      
      // Use the context function to add the match
      const result = await addVGCMatch(matchData);
      
      if (result && result.success) {
        syncedCount++;
        successfulSyncs.vgcMatches.push(match._id);
        console.log(`Successfully synced VGC match: ${match._id}`);
      } else {
        throw new Error(result?.error || 'Failed to sync match');
      }
    } catch (error) {
      console.error(`Failed to sync VGC match ${match._id}:`, error);
      errors.push(`VGC match from ${match.createdAt}: ${error.message}`);
    }
  }

  // Sync decks
  for (const deck of decks) {
    try {
      // Remove local-specific fields before syncing
      const { _id, isLocal, createdAt, updatedAt, __v, ...deckData } = deck;
      
      // Clean up card data to ensure proper structure
      const cleanCards = (cards) => {
        if (!Array.isArray(cards)) return [];
        return cards.map(card => {
          const { _id, ...cardData } = card;
          // Ensure required fields
          if (!cardData.id) cardData.id = `${cardData.name}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          if (!cardData.supertype) {
            // Determine supertype based on card name or default
            if (cardData.name && cardData.name.toLowerCase().includes('energy')) {
              cardData.supertype = 'Energy';
            } else if (cardData.name && (cardData.name.includes('Professor') || cardData.name.includes('Item') || cardData.name.includes('Supporter'))) {
              cardData.supertype = 'Trainer';
            } else {
              cardData.supertype = 'Pokémon';
            }
          }
          if (!cardData.count && cardData.quantity) cardData.count = cardData.quantity;
          if (!cardData.quantity && cardData.count) cardData.quantity = cardData.count;
          if (!cardData.count) cardData.count = 1;
          if (!cardData.quantity) cardData.quantity = 1;
          return cardData;
        });
      };
      
      // Clean up deck data with appropriate supertypes
      deckData.pokemon = cleanCards(deckData.pokemon || []).map(card => ({ ...card, supertype: 'Pokémon' }));
      deckData.trainers = cleanCards(deckData.trainers || []).map(card => ({ ...card, supertype: 'Trainer' }));
      deckData.energy = cleanCards(deckData.energy || []).map(card => ({ ...card, supertype: 'Energy' }));
      
      // Validate deck has required fields
      if (!deckData.name) {
        throw new Error('Deck name is required');
      }
      
      // Add user ID if provided (for cases where DeckState doesn't have access to user context)
      if (userId) {
        deckData.user = userId;
      }
      
      // Log the deck data being synced
      console.log('Syncing deck with cleaned data:', deckData);
      
      // Use the context function to add the deck
      try {
        console.log('Attempting to sync deck:', deck._id);
        console.log('Deck data being sent:', deckData);
        
        let result;
        try {
          result = await addDeck(deckData);
          console.log('AddDeck function result:', result);
        } catch (contextError) {
          console.warn('AddDeck function failed, trying direct API call:', contextError);
          // Fallback to direct API call if context function fails
          const response = await api.post('/api/decks', deckData);
          result = response.data;
          console.log('Direct API call result:', result);
        }
        
        console.log('Final deck result:', result);
        console.log('Final deck result type:', typeof result);
        
        if (result && (result._id || result.success !== false)) {
          // Consider it successful if we get a result with an ID or if success is not explicitly false
          syncedCount++;
          successfulSyncs.decks.push(deck._id);
          console.log(`Successfully synced deck: ${deck._id}, result:`, result);
        } else {
          console.warn(`Deck sync failed or returned invalid result for: ${deck._id}`, result);
          failedSyncs.decks.push({
            id: deck._id,
            error: 'Invalid result from addDeck',
            result: result
          });
        }
      } catch (deckError) {
        console.error('Deck sync error details:', {
          error: deckError,
          response: deckError.response?.data,
          status: deckError.response?.status,
          message: deckError.message,
          deckData: deckData
        });
        
        // Check if it's a validation error we can handle
        if (deckError.response?.status === 400) {
          console.error('Deck validation failed:', deckError.response.data);
        }
        
        // Add to failed syncs instead of throwing
        failedSyncs.decks.push({
          id: deck._id,
          error: deckError.message || 'Unknown error',
          statusCode: deckError.response?.status,
          serverResponse: deckError.response?.data
        });
      }
    } catch (error) {
      console.error(`Failed to sync deck ${deck._id}:`, error);
      // Log more detailed error information
      if (error.response) {
        console.error('Server response:', error.response.status, error.response.data);
      }
      errors.push(`Deck "${deck.name}" from ${deck.createdAt}: ${error.message}`);
    }
  }

  // Sync teams
  for (const team of teams) {
    try {
      // Remove local-specific fields before syncing
      const { _id, isLocal, createdAt, ...teamData } = team;
      
      // Add user ID if provided
      if (userId) {
        teamData.user = userId;
      }
      
      // Use the context function to add the team
      const result = await addTeam(teamData);
      
      if (result) {
        syncedCount++;
        successfulSyncs.teams.push(team._id);
        console.log(`Successfully synced team: ${team._id}`);
      } else {
        throw new Error('Failed to sync team');
      }
    } catch (error) {
      console.error(`Failed to sync team ${team._id}:`, error);
      errors.push(`Team "${team.name}" from ${team.createdAt}: ${error.message}`);
    }
  }

  // Remove successfully synced items from local storage
  if (syncedCount > 0) {
    // Remove successfully synced TCG matches
    if (successfulSyncs.tcgMatches.length > 0) {
      const remainingTCGMatches = tcgMatches.filter(m => !successfulSyncs.tcgMatches.includes(m._id));
      if (remainingTCGMatches.length === 0) {
        localStorage.removeItem(LOCAL_TCG_MATCHES_KEY);
      } else {
        localStorage.setItem(LOCAL_TCG_MATCHES_KEY, JSON.stringify(remainingTCGMatches));
      }
      console.log(`Removed ${successfulSyncs.tcgMatches.length} synced TCG matches from local storage`);
    }
    
    // Remove successfully synced VGC matches
    if (successfulSyncs.vgcMatches.length > 0) {
      const remainingVGCMatches = vgcMatches.filter(m => !successfulSyncs.vgcMatches.includes(m._id));
      if (remainingVGCMatches.length === 0) {
        localStorage.removeItem(LOCAL_VGC_MATCHES_KEY);
      } else {
        localStorage.setItem(LOCAL_VGC_MATCHES_KEY, JSON.stringify(remainingVGCMatches));
      }
      console.log(`Removed ${successfulSyncs.vgcMatches.length} synced VGC matches from local storage`);
    }
    
    // Remove successfully synced decks
    if (successfulSyncs.decks.length > 0) {
      const remainingDecks = decks.filter(d => !successfulSyncs.decks.includes(d._id));
      if (remainingDecks.length === 0) {
        localStorage.removeItem(LOCAL_DECKS_KEY);
      } else {
        localStorage.setItem(LOCAL_DECKS_KEY, JSON.stringify(remainingDecks));
      }
      console.log(`Removed ${successfulSyncs.decks.length} synced decks from local storage`);
    }
    
    // Remove successfully synced teams
    if (successfulSyncs.teams.length > 0) {
      const remainingTeams = teams.filter(t => !successfulSyncs.teams.includes(t._id));
      if (remainingTeams.length === 0) {
        localStorage.removeItem(LOCAL_TEAMS_KEY);
      } else {
        localStorage.setItem(LOCAL_TEAMS_KEY, JSON.stringify(remainingTeams));
      }
      console.log(`Removed ${successfulSyncs.teams.length} synced teams from local storage`);
    }
    
    // Clear local active deck if all local decks were synced
    // Note: We don't set a new active deck here since the user may already have 
    // an active deck on the server, and we should respect their existing choice
    if (successfulSyncs.decks.length === decks.length) {
      localStorage.removeItem(LOCAL_ACTIVE_DECK_KEY);
      console.log('Cleared local active deck after syncing all local decks');
    }
  }

  if (errors.length === 0) {
    console.log(`Successfully synced all ${syncedCount} items`);
  } else {
    console.warn(`Synced ${syncedCount} items, but ${errors.length} failed.`);
  }

  return {
    syncedCount,
    totalItems: tcgMatches.length + vgcMatches.length + decks.length + teams.length,
    errors
  };
};

// Active Deck Functions for non-authenticated users
export const getLocalActiveDeck = () => {
  try {
    return localStorage.getItem(LOCAL_ACTIVE_DECK_KEY);
  } catch (error) {
    console.error('Error getting local active deck:', error);
    return null;
  }
};

export const setLocalActiveDeck = (deckId) => {
  try {
    if (deckId) {
      localStorage.setItem(LOCAL_ACTIVE_DECK_KEY, deckId);
    } else {
      localStorage.removeItem(LOCAL_ACTIVE_DECK_KEY);
    }
    return true;
  } catch (error) {
    console.error('Error setting local active deck:', error);
    return false;
  }
};

export const clearLocalActiveDeck = () => {
  try {
    localStorage.removeItem(LOCAL_ACTIVE_DECK_KEY);
    return true;
  } catch (error) {
    console.error('Error clearing local active deck:', error);
    return false;
  }
};

// TCG Match Draft Functions
export const getTCGDraft = () => {
  try {
    const draft = localStorage.getItem(LOCAL_TCG_DRAFT_KEY);
    return draft ? JSON.parse(draft) : null;
  } catch (error) {
    console.error('Error getting TCG draft:', error);
    return null;
  }
};

export const saveTCGDraft = (draftData) => {
  try {
    const draft = {
      ...draftData,
      savedAt: new Date().toISOString(),
      isDraft: true
    };
    localStorage.setItem(LOCAL_TCG_DRAFT_KEY, JSON.stringify(draft));
    return true;
  } catch (error) {
    console.error('Error saving TCG draft:', error);
    return false;
  }
};

export const clearTCGDraft = () => {
  try {
    localStorage.removeItem(LOCAL_TCG_DRAFT_KEY);
    return true;
  } catch (error) {
    console.error('Error clearing TCG draft:', error);
    return false;
  }
};